// src/pages/ProductPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firestore";
import { useCart } from "../context/cartContext";
import { useUser } from "../context/userContext";
import { Star, Minus, Plus, ShoppingCart, Package, Check, Heart } from "lucide-react";
import FavoriteButton from "../Components/FavoriteButton";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, addToCart, updateCartItemQuantity } = useCart();
  const { id: userId, loading: userLoading, initialized, telegramUser } = useUser();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [product, setProduct] = useState(location.state?.product || null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const cartItem = cartItems.find((item) => item.id === id);

  // Telegram back button
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.BackButton.show();
      tg.BackButton.onClick(() => navigate(-1));
      return () => {
        tg.BackButton.hide();
        tg.BackButton.offClick();
      };
    }
  }, [navigate]);

  // Scroll to top on mount
  useEffect(() => {
    const el = document.getElementById("refer");
    if (el) el.scrollTop = 0;
  }, []);

  // Fetch product from Firestore
  useEffect(() => {
    if (!product) {
      const fetchProduct = async () => {
        setLoadingProduct(true);
        try {
          const productRef = doc(db, "products", id);
          const snap = await getDoc(productRef);
          if (snap.exists()) {
            const data = snap.data();
            setProduct({ 
              id: snap.id, 
              ...data
            });
          } else {
            console.error("Product not found");
          }
        } catch (error) {
          console.error("Error fetching product:", error);
        } finally {
          setLoadingProduct(false);
        }
      };
      fetchProduct();
    }
  }, [id, product]);

  if (loadingProduct || !product) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Загрузка товара...</p>
        </div>
      </div>
    );
  }

  // Правильное преобразование discount к числу
  const discountValue = product.discount !== undefined && product.discount !== null 
    ? Number(product.discount) 
    : 0;
  
  // Правильная проверка: есть discount и он больше 0
  const hasDiscount = !isNaN(discountValue) && discountValue > 0;
  
  const discountedPrice = hasDiscount
    ? Math.round((product.price * (100 - discountValue)) / 100)
    : product.price;

  const formatPrice = (price) => {
    return price.toLocaleString('ru-RU');
  };

  const oldPrice = hasDiscount 
    ? Math.round(product.price)
    : product.price;

  // Проверяем, есть ли рейтинг для отображения
  const hasRating = product.rating && product.rating > 0;
  
  // Объединенные данные из описания и характеристик
  const getCombinedContent = () => {
    const content = [];
    
    // Основное описание
    const description = product.fullDescription || product.description;
    if (description) {
      content.push({
        type: 'description',
        content: description
      });
    }
    
    // Характеристики
    if (product.specifications && product.specifications.length > 0) {
      content.push({
        type: 'specs',
        content: product.specifications
      });
    }
    
    return content;
  };

  const combinedContent = getCombinedContent();

  return (
    <div className="h-screen w-full overflow-hidden pt-12">
      <div
        id="refer"
        className="overflow-y-auto max-h-full px-4 pt-12 relative z-10 touch-pan-y overscroll-contain"
      >
        {/* Изображение товара с кнопкой "В избранное" */}
        <div className="mt-1 relative">
          <div className="w-full aspect-square rounded-xl overflow-hidden relative">
            <img
              src={product.image}
              alt={product.title}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className="w-full h-full object-cover select-none"
            />
            
            {/* Кнопка "В избранное" */}
            {product && (
              <div className="absolute top-3 right-3 z-20 pointer-events-auto">
                <div className="w-10 h-10 bg-black/30 hover:bg-black/40 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110 active:scale-95 cursor-pointer">
                  {!initialized ? (
                    <Heart className="w-5 h-5 text-white animate-pulse" />
                  ) : !telegramUser ? (
                    <Heart className="w-5 h-5 text-white/50" />
                  ) : (
                    <FavoriteButton product={product} size="large" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Title и категория компактно */}
        <div className="mt-4">
{product.categories && product.categories.length > 0 && (
  <p className="text-xs text-gray-500 mb-1">
    {product.categories.map(cat => cat.name || cat.title).join(" • ")}
  </p>
)}
          
          <h1 className="text-xl font-bold leading-tight break-words">
            {product.title}
          </h1>
        </div>

        {/* Price и рейтинг в одну строку */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">
              {formatPrice(discountedPrice)} ₽
            </span>
            
            {/* Правильное условие: discount существует и больше 0 */}
            {hasDiscount && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md">
                -{discountValue}%
              </span>
            )}
          </div>
          
          {/* Рейтинг - показываем только если есть значение больше 0 */}
          {hasRating && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
              <span className="text-gray-700 text-sm font-medium">
                {product.rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>
        
        {/* Старая цена показываем только если есть скидка */}
        {hasDiscount && (
          <div className="mt-1">
            <span className="text-sm text-gray-400 line-through">
              {formatPrice(oldPrice)} ₽
            </span>
          </div>
        )}

        {/* Объединенный блок Описание + Комплектация */}
        <div className="mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Описание и характеристики</h2>

          <div className="space-y-4">
            {combinedContent.map((section, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                {section.type === 'description' && (
                  <div className="relative">
                    <p className={`text-gray-700 text-sm leading-relaxed ${
                      showFullDescription ? '' : 'line-clamp-3'
                    }`}>
                      {section.content}
                    </p>
                    
                    {section.content.length > 150 && (
                      <>
                        {!showFullDescription && (
                          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 to-transparent" />
                        )}
                        
                        <button
                          onClick={() => setShowFullDescription(!showFullDescription)}
                          className="mt-2 text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors"
                        >
                          {showFullDescription ? 'Свернуть' : 'Раскрыть полностью'}
                        </button>
                      </>
                    )}
                  </div>
                )}
                
                {section.type === 'specs' && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-gray-900 text-sm mb-2">Комплектация</h3>
                    <div className="space-y-1">
                      {section.content.map((spec, i) => (
                        <div key={i} className="flex items-center py-1">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                          <div className="flex-1">
                            <div className="text-gray-900 text-sm">{spec.key}</div>
                            <div className="text-gray-600 text-xs">{spec.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Дополнительная информация если нет данных */}
          {combinedContent.length === 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-600 text-sm">
                Высококачественный продукт с оптимальным соотношением цены и качества. 
                Все товары проходят строгий контроль перед отправкой.
              </p>
            </div>
          )}
        </div>

        {/* Гарантии и преимущества компактно */}
        <div className="mt-6 mb-20">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Гарантии</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-green-600" />
              </div>
              <span className="text-gray-700 text-sm">Оригинальная продукция</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="w-3 h-3 text-blue-600" />
              </div>
              <span className="text-gray-700 text-sm">Надежная упаковка</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-gray-700 text-sm">Гарантия качества</span>
            </div>
          </div>
        </div>

        {/* Spacer for fixed button */}
        <div className="h-[120px]" />
      </div>

      {/* Fixed button at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30">
        <div className="max-w-md mx-auto">
          {cartItem ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center bg-gray-100 rounded-xl px-3 py-2">
                <button
                  onClick={() =>
                    updateCartItemQuantity(
                      product.id,
                      Math.max(cartItem.quantity - 1, 1)
                    )
                  }
                  className="text-gray-600 hover:text-black p-1"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 text-lg font-bold min-w-[36px] text-center">
                  {cartItem.quantity}
                </span>
                <button
                  onClick={() =>
                    updateCartItemQuantity(product.id, cartItem.quantity + 1)
                  }
                  className="text-gray-600 hover:text-black p-1"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={() => navigate("/cart")}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-xl text-base font-bold flex items-center justify-center gap-2 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                В корзине
              </button>
            </div>
          ) : (
            <button
              onClick={() => addToCart(product)}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-xl text-base font-bold transition-colors active:scale-95"
            >
              Добавить в корзину
            </button>
          )}
          
          {/* Статус наличия */}
          <div className="mt-2 text-center">
            {product.inStock !== false ? (
              <span className="text-green-600 text-sm font-medium">
                ✓ В наличии
              </span>
            ) : (
              <span className="text-red-600 text-sm font-medium">
                ✗ Нет в наличии
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;